import classNames from 'classnames';
import { memo, useState } from 'react';
import { useSelector } from 'react-redux';
import { Link } from 'react-router';

import { onlineCourseShareActivityButtonClicked } from 'my-core/gtm-events';
import { useSiteTourStep } from 'my-core/hooks';
import useOnlineCourseBookletPrint from 'my-core/hooks/useOnlineCourseBookletPrint';
import { getActivityTypeConfig } from 'my-core/online-course-activity-utils';
import { getNodeTitleText } from 'my-core/online-course-content-map';
import { ACCESS_LEVELS } from 'my-core/online-course-utils';
import { SITE_TOURS } from 'my-core/site-tour';

import ShareActivityDialog from 'my-components/ShareActivityDialog';
import LatexString from 'my-elements/LatexString';

import InCourseExperienceHeaderWrapper from './InCourseExperienceHeaderWrapper';

import Button from '@mui/material/Button';
import ButtonBase from '@mui/material/ButtonBase';
import Divider from '@mui/material/Divider';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';

import {
  faArrowLeft,
  faArrowRight,
  faBars,
  faBookmark,
  faCommentAltSmile,
  faCommentExclamation,
  faHome,
  faPen,
  faPrint,
  faShare,
} from '@fortawesome/pro-regular-svg-icons';
import { faBookmark as faBookmarkSolid } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

const rootCss = ({ breakpoints, palette, spacing, typography }) => ({
  '.ICEH-leftActions': { flex: '0 0 auto', display: 'flex', alignItems: 'center' },
  '.ICEH-iconButton_active': { color: palette.primary.main },
  '.ICEH-rightActions': { display: 'flex', flex: '0 0 auto', gap: spacing(0.5) },
  '.ICEH-button': { minWidth: 32, height: 32, borderRadius: 16 },
  '.ICEH-button_hidingText': {
    '& > span': {
      marginRight: spacing(1),
      [breakpoints.only('xs')]: { display: 'none' },
    },
  },
  '.ICEH-title': {
    flex: 1,
    minWidth: 0,
    margin: spacing(0, 1),
    display: '-webkit-box',
    WebkitLineClamp: 2,
    WebkitBoxOrient: 'vertical',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    lineHeight: 1.4,
    [breakpoints.only('xs')]: {
      '&.ICEH-title_h6': { fontSize: typography.body1.fontSize },
      fontSize: typography.caption.fontSize,
    },
  },
  '.ICEH-divider': { height: 20, marginLeft: spacing(2) },
});

export default memo(function InCourseExperienceHeader(props) {
  const {
    activeComponent,
    activityFavorited,
    canShare: canShareProp,
    drawerOpen,
    editPath,
    enableTour,
    homeLabel,
    homePath,
    learningModePath,
    learnModePath,
    nextAndPrev,
    onlineCourse,
    onShowFeedback,
    onToggleDrawer,
    onToggleFavorite,
    practiceModePath,
    printPath,
    rightActions,
    title: titleProp,
    titleVariant,
  } = props;
  const { next, prev } = nextAndPrev || {};

  const canShare = canShareProp && activeComponent?.type === 'activity';
  const [shareDialogOpen, setShareDialogOpen] = useState(false);

  const { id: tourId, steps } = SITE_TOURS.ONLINE_COURSE_EXPERIENCE;
  const tourRunning = useSelector(state => state.siteTours.status.running);
  const menuBtnId = useSiteTourStep({
    disabled: !(enableTour && onToggleDrawer && (tourRunning || !drawerOpen)),
    step: steps.menu,
    tourId,
  });

  const homeBtnId = useSiteTourStep({ disabled: !(enableTour && homePath), step: steps.home, tourId });
  const printBtnId = useSiteTourStep({ disabled: !(enableTour && printPath), step: steps.print, tourId });
  const shareBtnId = useSiteTourStep({ disabled: !(enableTour && canShare), step: steps.share, tourId });

  const [printBooklet, fetchingBooklet] = useOnlineCourseBookletPrint();

  return (
    <InCourseExperienceHeaderWrapper css={rootCss}>
      <div className="ICEH-leftActions">{renderActions()}</div>
      <Typography
        className={classNames('ICEH-title', { [`ICEH-title_${titleVariant}`]: titleVariant })}
        component="h1"
        variant={titleVariant || 'body2'}
      >
        {titleProp || (
          <LatexString>{`${activeComponent.fullPos || ''} ${getNodeTitleText(activeComponent)}`}</LatexString>
        )}
      </Typography>

      <div className="ICEH-rightActions">
        {rightActions}
        {prev && (
          <Tooltip
            title={
              prev.node ?
                <>
                  {prev.node?.type === 'activity' && (
                    <FontAwesomeIcon icon={getActivityTypeConfig(prev.node.data).icon} style={{ marginRight: 4 }} />
                  )}
                  {prev.node.data.title}
                </>
              : ''
            }
          >
            <Button className="ICEH-button" component={Link} nativeButton={false} to={prev.path} variant="outlined">
              <FontAwesomeIcon icon={faArrowLeft} />
            </Button>
          </Tooltip>
        )}
        {next ?
          <Tooltip
            title={
              next.node ?
                <>
                  {next.node?.type === 'activity' && (
                    <FontAwesomeIcon icon={getActivityTypeConfig(next.node.data).icon} style={{ marginRight: 4 }} />
                  )}
                  {next.node.data.title}
                </>
              : null
            }
          >
            <Button
              className="ICEH-button ICEH-button_hidingText"
              component={Link}
              nativeButton={false}
              to={next.path}
              variant="contained"
            >
              <span>Next</span>
              <FontAwesomeIcon icon={faArrowRight} />
            </Button>
          </Tooltip>
        : homePath ?
          <Button
            className="ICEH-button ICEH-button_hidingText"
            component={Link}
            nativeButton={false}
            to={homePath}
            variant="outlined"
          >
            <span>{homeLabel || 'Home'}</span>
            <FontAwesomeIcon icon={faHome} />
          </Button>
        : onShowFeedback ?
          <Button className="ICEH-button ICEH-button_hidingText" onClick={onShowFeedback} variant="outlined">
            <span>Feedback</span>
            <FontAwesomeIcon icon={faCommentAltSmile} />
          </Button>
        : learningModePath && (
            <Button
              className="ICEH-button"
              component={Link}
              nativeButton={false}
              to={learningModePath}
              variant="outlined"
            >
              Go To Learn Mode
            </Button>
          )
        }
      </div>
    </InCourseExperienceHeaderWrapper>
  );

  function renderActions() {
    return (
      <>
        {onToggleDrawer && (tourRunning || !drawerOpen) && (
          <Tooltip enterDelay={700} placement="bottom" title="Curriculum">
            <IconButton
              className={classNames({ 'ICEH-iconButton_active': drawerOpen })}
              id={menuBtnId}
              onClick={onToggleDrawer}
            >
              <FontAwesomeIcon icon={faBars} size="xs" />
            </IconButton>
          </Tooltip>
        )}
        {(learnModePath || practiceModePath) && (
          <>
            <LearnPracticeModeToggle learnModePath={learnModePath} practiceModePath={practiceModePath} />
            <Divider className="ICEH-divider" orientation="vertical" />
          </>
        )}
        {homePath && (
          <Tooltip enterDelay={700} placement="bottom" title={homeLabel || 'Course Overview'}>
            <IconButton component={Link} id={homeBtnId} nativeButton={false} to={homePath}>
              <FontAwesomeIcon icon={faHome} size="xs" />
            </IconButton>
          </Tooltip>
        )}
        {onShowFeedback && (
          <Tooltip enterDelay={700} title="Leave Feedback">
            <IconButton onClick={onShowFeedback}>
              <FontAwesomeIcon icon={faCommentExclamation} size="xs" />
            </IconButton>
          </Tooltip>
        )}
        {printPath && (
          <Tooltip enterDelay={700} placement="bottom" title="Print Chapter">
            <IconButton
              disabled={fetchingBooklet}
              id={printBtnId}
              onClick={() => printBooklet(onlineCourse.id, printPath)}
            >
              <FontAwesomeIcon icon={faPrint} size="xs" />
            </IconButton>
          </Tooltip>
        )}
        {editPath && onlineCourse?.user_access_level >= ACCESS_LEVELS.EDIT && (
          <Tooltip enterDelay={700} placement="bottom" title="Edit Activity">
            <IconButton component={Link} nativeButton={false} to={editPath}>
              <FontAwesomeIcon icon={faPen} size="xs" />
            </IconButton>
          </Tooltip>
        )}
        {canShare && (
          <>
            <Tooltip enterDelay={700} placement="bottom" title="Share With a Classmate">
              <IconButton
                id={shareBtnId}
                onClick={() => {
                  onlineCourseShareActivityButtonClicked(onlineCourse);
                  setShareDialogOpen(true);
                }}
              >
                <FontAwesomeIcon icon={faShare} size="xs" />
              </IconButton>
            </Tooltip>
            <ShareActivityDialog
              key={activeComponent.data.id}
              onClose={() => setShareDialogOpen(false)}
              onlineCourse={onlineCourse}
              onlineCourseActivityId={activeComponent.data.id}
              open={shareDialogOpen}
            />
          </>
        )}
        {onToggleFavorite && (
          <Tooltip enterDelay={700} placement="bottom" title="Bookmark this activity">
            <IconButton onClick={() => onToggleFavorite(!activityFavorited)}>
              <FontAwesomeIcon icon={activityFavorited ? faBookmarkSolid : faBookmark} size="xs" />
            </IconButton>
          </Tooltip>
        )}
      </>
    );
  }
});

const LearnPracticeModeToggle = memo(function LearnPracticeModeToggle({ learnModePath, practiceModePath }) {
  return (
    <div
      css={({ breakpoints, palette, shape: { borderRadius }, spacing, typography }) => ({
        border: `1px solid ${palette.divider}`,
        padding: spacing(0.5),
        borderRadius,
        backgroundColor: palette.background.paper1,
        '& > .toggleBtn': {
          whiteSpace: 'pre',
          padding: spacing(0.5, 2),
          borderRadius,
          ...typography.body1,
          color: palette.text.secondary,
          '&.toggleBtn_active': {
            ...typography.subtitle1,
            border: `1px solid ${palette.divider}`,
            backgroundColor: palette.background.paper,
            '&.toggleBtn_learn': { color: palette.primary.main },
            '&.toggleBtn_practice': { color: palette.green.main },
          },
        },
        [breakpoints.down('md')]: {
          '& > .toggleBtn': {
            ...typography.body2,
            padding: spacing(0.25, 1),
            '&.toggleBtn_active': { ...typography.subtitle2 },
          },
        },
        [breakpoints.down('sm')]: { '& .smHide': { display: 'none' } },
      })}
    >
      <ButtonBase
        className={classNames('toggleBtn', 'toggleBtn_learn', { toggleBtn_active: practiceModePath })}
        component={learnModePath ? Link : undefined}
        disabled={!!practiceModePath}
        nativeButton={learnModePath ? false : undefined}
        to={learnModePath}
      >
        Learn <span className="smHide">Mode</span>
      </ButtonBase>
      <ButtonBase
        className={classNames('toggleBtn', 'toggleBtn_practice', { toggleBtn_active: learnModePath })}
        component={practiceModePath ? Link : undefined}
        disabled={!!learnModePath}
        nativeButton={practiceModePath ? false : undefined}
        to={practiceModePath}
      >
        Practice <span className="smHide">Mode</span>
      </ButtonBase>
    </div>
  );
});
// InCourseExperienceHeader.propTypes = {
//   activeComponent: PropTypes.object,
//   canShare: PropTypes.bool,
//   drawerOpen: PropTypes.bool,
//   editPath: PropTypes.oneOfType([PropTypes.object, PropTypes.string]),
//   homePath: PropTypes.oneOfType([PropTypes.object, PropTypes.string]),
//   learningModePath: PropTypes.string,
//   nextAndPrev: PropTypes.object,
//   onlineCourse: PropTypes.object,
//   onShowFeedback: PropTypes.func,
//   onToggleDrawer: PropTypes.func,
//   printPath: PropTypes.oneOfType([PropTypes.object, PropTypes.string, PropTypes.bool]),
//   title: PropTypes.node,
// };
